import { createSlice } from '@reduxjs/toolkit';

// Load cart from localStorage on initialization
const loadCartFromStorage = () => {
  try {
    const serializedCart = localStorage.getItem('shilpani_cart');
    if (serializedCart === null) {
      return [];
    }
    return JSON.parse(serializedCart);
  } catch (err) {
    console.error('Error loading cart from localStorage:', err);
    return [];
  }
};

// Save cart to localStorage
const saveCartToStorage = (cart) => {
  try {
    const serializedCart = JSON.stringify(cart);
    localStorage.setItem('shilpani_cart', serializedCart);
  } catch (err) {
    console.error('Error saving cart to localStorage:', err);
  }
};

// Load tenant ID from localStorage
const loadCartTenantId = () => {
  try {
    return localStorage.getItem('cart_tenantId') || null;
  } catch (err) {
    return null;
  }
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: loadCartFromStorage(),
    tenantId: loadCartTenantId(), // Store tenant ID to validate cart items belong to same tenant
  },
  reducers: {
    addToCart: (state, action) => {
      const { product, quantity = 1, tenantId } = action.payload;
      
      // Validate tenant: if cart has items from different tenant, clear cart first
      if (state.tenantId && tenantId && state.tenantId !== tenantId) {
        console.warn('[Cart] Different tenant detected, clearing cart');
        state.items = [];
        state.tenantId = tenantId;
      } else if (!state.tenantId && tenantId) {
        // Set tenant ID if cart is empty
        state.tenantId = tenantId;
      }
      
      // Create unique key for cart item (product ID + variant attributes)
      const variantKey = product.variant?.attributes 
        ? product.variant.attributes.map(a => `${a.attributeName}:${a.value}`).sort().join('|')
        : '';
      const itemKey = `${product._id}${variantKey ? `_${variantKey}` : ''}`;
      
      const existingItem = state.items.find(item => {
        const itemVariantKey = item.variant?.attributes
          ? item.variant.attributes.map(a => `${a.attributeName}:${a.value}`).sort().join('|')
          : '';
        const existingKey = `${item.product._id}${itemVariantKey ? `_${itemVariantKey}` : ''}`;
        return existingKey === itemKey;
      });

      if (existingItem) {
        // If same product with same variant already in cart, increase quantity
        existingItem.quantity += quantity;
        // Ensure quantity doesn't exceed stock
        if (product.stock && existingItem.quantity > product.stock) {
          existingItem.quantity = product.stock;
        }
      } else {
        // Add new item to cart
        state.items.push({
          product: {
            _id: product._id,
            name: product.name,
            price: product.price,
            discountedPrice: product.discountedPrice,
            discount: product.discount,
            currency: product.currency,
            images: product.images,
            stock: product.stock,
            category: product.category,
            weight: product.weight || 0,
            dimensions: product.dimensions || { length: 0, width: 0, height: 0 }
          },
          variant: product.variant || null,
          variantAttributes: product.variantAttributes || [],
          quantity: Math.min(quantity, product.stock || quantity)
        });
      }
      saveCartToStorage(state.items);
      // Save tenant ID to localStorage for persistence
      if (tenantId) {
        localStorage.setItem('cart_tenantId', tenantId);
      }
    },
    removeFromCart: (state, action) => {
      const productId = action.payload;
      state.items = state.items.filter(item => item.product._id !== productId);
      saveCartToStorage(state.items);
    },
    updateQuantity: (state, action) => {
      const { productId, quantity } = action.payload;
      const item = state.items.find(item => item.product._id === productId);
      if (item) {
        if (quantity <= 0) {
          // Remove item if quantity is 0 or less
          state.items = state.items.filter(item => item.product._id !== productId);
        } else {
          // Ensure quantity doesn't exceed stock
          const maxQuantity = item.product.stock || quantity;
          item.quantity = Math.min(quantity, maxQuantity);
        }
      }
      saveCartToStorage(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      state.tenantId = null;
      saveCartToStorage(state.items);
      localStorage.removeItem('cart_tenantId');
    },
    syncCartWithProducts: (state, action) => {
      // Sync cart items with latest product data (for price/stock updates)
      const products = action.payload;
      state.items = state.items.map(item => {
        const updatedProduct = products.find(p => p._id === item.product._id);
        if (updatedProduct) {
          return {
            ...item,
            product: {
              ...item.product,
              price: updatedProduct.price,
              discountedPrice: updatedProduct.discountedPrice,
              discount: updatedProduct.discount,
              stock: updatedProduct.stock,
              images: updatedProduct.images,
              weight: updatedProduct.weight || item.product.weight || 0,
              dimensions: updatedProduct.dimensions || item.product.dimensions || { length: 0, width: 0, height: 0 }
            },
            // Adjust quantity if stock is less than current quantity
            quantity: Math.min(item.quantity, updatedProduct.stock || item.quantity)
          };
        }
        return item;
      }).filter(item => item.quantity > 0); // Remove items with 0 quantity
      saveCartToStorage(state.items);
    }
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart, syncCartWithProducts } = cartSlice.actions;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartItemCount = (state) => 
  state.cart.items.reduce((total, item) => total + item.quantity, 0);
export const selectCartTotal = (state) => 
  state.cart.items.reduce((total, item) => {
    const itemPrice = item.product.discountedPrice || item.product.price;
    return total + (itemPrice * item.quantity);
  }, 0);
export const selectCartCurrency = (state) => {
  const items = state.cart.items;
  if (items.length === 0) return 'INR';
  return items[0].product.currency || 'INR';
};

export default cartSlice.reducer;

